import React from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, fetchApiRef } from '@backstage/core-plugin-api';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import ScheduleIcon from '@material-ui/icons/Schedule';
import RefreshIcon from '@material-ui/icons/Refresh';
import LanguageIcon from '@material-ui/icons/Language';
import LockIcon from '@material-ui/icons/Lock';
import DnsIcon from '@material-ui/icons/Dns';
import HttpsIcon from '@material-ui/icons/Http';

/* ─── Types ─── */

interface InfraStatus {
  ingress: { exists: boolean; ip: string | null; host: string | null };
  cert: { exists: boolean; status: string | null };
  dns: { resolved: boolean; addresses: string[] };
  https: { statusCode: number | null; ok: boolean; error?: string };
}

/* ─── Styles ─── */

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 0),
  },
  labelBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minWidth: 120,
  },
  label: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  value: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.8rem',
  },
  mono: {
    fontFamily: 'monospace',
    fontSize: '0.78rem',
  },
  empty: {
    textAlign: 'center',
    padding: theme.spacing(2, 0),
    color: theme.palette.text.hint,
    fontSize: '0.8rem',
  },
}));

/* ─── Helpers ─── */

function StatusIcon({
  variant,
  size = 16,
}: {
  variant: 'ok' | 'progress' | 'error' | 'unknown';
  size?: number;
}) {
  switch (variant) {
    case 'ok':
      return <CheckCircleIcon style={{ fontSize: size, color: '#4caf50' }} />;
    case 'progress':
      return <ScheduleIcon style={{ fontSize: size, color: '#ff9800' }} />;
    case 'error':
      return <ErrorIcon style={{ fontSize: size, color: '#f44336' }} />;
    default:
      return <ScheduleIcon style={{ fontSize: size, color: '#bdbdbd' }} />;
  }
}

function certVariant(status: string | null): 'ok' | 'progress' | 'error' {
  if (!status) return 'error';
  switch (status) {
    case 'Active':
      return 'ok';
    case 'Provisioning':
      return 'progress';
    default:
      return 'error';
  }
}

function certLabelKo(status: string | null): string {
  if (!status) return '없음';
  switch (status) {
    case 'Active':
      return '활성';
    case 'Provisioning':
      return '프로비저닝 중';
    case 'FailedNotVisible':
      return '실패 (도메인 미검증)';
    default:
      return status;
  }
}

function httpsVariant(
  statusCode: number | null,
): 'ok' | 'progress' | 'error' {
  if (statusCode === null) return 'error';
  if (statusCode >= 200 && statusCode < 500) return 'ok';
  if (statusCode === 502 || statusCode === 503) return 'progress';
  return 'error';
}

function httpsLabelKo(
  statusCode: number | null,
  error?: string,
): string {
  if (statusCode === null) return error ? '타임아웃' : '미확인';
  if (statusCode >= 200 && statusCode < 300) return `${statusCode} 정상`;
  if (statusCode >= 300 && statusCode < 400) return `${statusCode} 리다이렉트`;
  if (statusCode === 401 || statusCode === 403) return `${statusCode} 응답 정상 (인증 필요)`;
  if (statusCode === 404) return `${statusCode} 응답 정상 (루트 미매핑)`;
  if (statusCode >= 400 && statusCode < 500) return `${statusCode} 응답 중`;
  if (statusCode === 502) return '502 백엔드 대기';
  if (statusCode === 503) return '503 서비스 준비 중';
  return `${statusCode}`;
}

/* ─── Component ─── */

export const InfrastructureStatusCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const fetchApi = useApi(fetchApiRef);

  const namespace =
    entity?.metadata?.annotations?.['backstage.io/kubernetes-namespace'] ?? '';
  const serviceName = entity?.metadata?.name ?? '';
  const kind = entity?.kind ?? '';

  const [data, setData] = React.useState<InfraStatus | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const fetchStatus = React.useCallback(async () => {
    if (!namespace || !serviceName) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchApi.fetch(
        `/api/kubernetes-infra/status?namespace=${encodeURIComponent(namespace)}&serviceName=${encodeURIComponent(serviceName)}`,
      );
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(
          (errBody as any)?.error || `HTTP ${resp.status}`,
        );
      }
      const json = await resp.json();
      setData(json as InfraStatus);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [fetchApi, namespace, serviceName]);

  React.useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  // DB(Resource) 등 Ingress 없는 엔티티는 숨김
  if (!namespace) return null;
  if (kind === 'Resource') return null;

  return (
    <Card>
      <CardHeader
        title="인프라 상태"
        titleTypographyProps={{ variant: 'subtitle1' }}
        action={
          <Tooltip title="새로고침">
            <IconButton size="small" onClick={fetchStatus}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      />
      <CardContent>
        {loading && !data ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        ) : error && !data ? (
          <Typography className={classes.empty}>{error}</Typography>
        ) : !data ? (
          <Typography className={classes.empty}>데이터 없음</Typography>
        ) : (
          <>
            {/* Ingress */}
            <Box className={classes.row}>
              <Box className={classes.labelBox}>
                <LanguageIcon
                  style={{ fontSize: 16, color: '#757575' }}
                />
                <Typography className={classes.label}>Ingress</Typography>
              </Box>
              <Box className={classes.value}>
                {data.ingress.exists ? (
                  <>
                    <StatusIcon
                      variant={data.ingress.ip ? 'ok' : 'progress'}
                    />
                    <Typography className={classes.mono}>
                      {data.ingress.ip ?? 'IP 할당 대기'}
                    </Typography>
                    {data.ingress.host && (
                      <Typography
                        className={classes.mono}
                        style={{ color: '#757575' }}
                      >
                        ({data.ingress.host})
                      </Typography>
                    )}
                  </>
                ) : (
                  <>
                    <StatusIcon variant="unknown" />
                    <Typography style={{ fontSize: '0.8rem', color: '#9e9e9e' }}>
                      미생성
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            {/* TLS Certificate */}
            <Box className={classes.row}>
              <Box className={classes.labelBox}>
                <LockIcon style={{ fontSize: 16, color: '#757575' }} />
                <Typography className={classes.label}>
                  TLS 인증서
                </Typography>
              </Box>
              <Box className={classes.value}>
                {data.cert.exists ? (
                  <>
                    <StatusIcon variant={certVariant(data.cert.status)} />
                    <Typography style={{ fontSize: '0.8rem' }}>
                      {certLabelKo(data.cert.status)}
                    </Typography>
                  </>
                ) : (
                  <>
                    <StatusIcon variant="unknown" />
                    <Typography style={{ fontSize: '0.8rem', color: '#9e9e9e' }}>
                      미생성
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            {/* DNS */}
            <Box className={classes.row}>
              <Box className={classes.labelBox}>
                <DnsIcon style={{ fontSize: 16, color: '#757575' }} />
                <Typography className={classes.label}>DNS</Typography>
              </Box>
              <Box className={classes.value}>
                {data.ingress.host ? (
                  <>
                    <StatusIcon
                      variant={data.dns.resolved ? 'ok' : 'progress'}
                    />
                    <Typography style={{ fontSize: '0.8rem' }}>
                      {data.dns.resolved ? 'Resolved' : '전파 대기'}
                    </Typography>
                  </>
                ) : (
                  <>
                    <StatusIcon variant="unknown" />
                    <Typography style={{ fontSize: '0.8rem', color: '#9e9e9e' }}>
                      호스트 없음
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            {/* HTTPS */}
            <Box className={classes.row}>
              <Box className={classes.labelBox}>
                <HttpsIcon style={{ fontSize: 16, color: '#757575' }} />
                <Typography className={classes.label}>HTTPS</Typography>
              </Box>
              <Box className={classes.value}>
                {data.ingress.host ? (
                  <>
                    <StatusIcon
                      variant={httpsVariant(data.https.statusCode)}
                    />
                    <Typography style={{ fontSize: '0.8rem' }}>
                      {httpsLabelKo(
                        data.https.statusCode,
                        data.https.error,
                      )}
                    </Typography>
                  </>
                ) : (
                  <>
                    <StatusIcon variant="unknown" />
                    <Typography style={{ fontSize: '0.8rem', color: '#9e9e9e' }}>
                      호스트 없음
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};
